import React, {useContext, useEffect, useState} from 'react';
import './Clusters.scss';
import {styled} from '@mui/material/styles';
import Map from './Components/Map/Map';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chart from '../Chart/Chart';
import axios from 'axios';
import {authContext} from '../Context/Authentication/authContext';
import {useDebounce} from '../SideMenu/useDebounce';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Grid,
  ListSubheader,
  Divider
} from '@mui/material';
import {SelectChangeEvent} from '@mui/material/Select';
import {baseURL} from '../config';
import {OPTIONS_TABS} from '../WhatIfAnalysis/helper'
import {generateChartData} from '../Helpers/helpers';
import {Bars} from 'react-loader-spinner'

const iOSBoxShadow =
  '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)';

const IOSSlider = styled(Slider)(({theme}) => ({
  color: theme.palette.mode === 'dark' ? '#f7b047' : '#f7b047',
  height: 2,
  padding: '15px 0',
  '& .MuiSlider-thumb': {
    height: 15,
    width: 15,
    backgroundColor: '#f7b047',
    boxShadow: iOSBoxShadow,
    '&:focus, &:hover, &.Mui-active': {
      boxShadow:
        '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)',
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        boxShadow: iOSBoxShadow,
      },
    },
  },
  '& .MuiSlider-valueLabel': {
    fontSize: 12,
    fontWeight: 'normal',
    top: -6,
    backgroundColor: 'unset',
    color: theme.palette.text.primary,
    '&:before': {
      display: 'none',
    },
    '& *': {
      background: 'transparent',
      color: theme.palette.mode === 'dark' ? '#fff' : '#f7b047',
    },
  },
  '& .MuiSlider-track': {
    color: '#f7b047',
  },
  '& .MuiSlider-rail': {
    opacity: 0.4,
    border: '1px solid #bfbfbf',
    backgroundColor: '#bfbfbf',
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#bfbfbf',
    height: 8,
    width: 1,
    '&.MuiSlider-markActive': {
      opacity: 1,
      color: 'rgb(255, 75, 75)',
      backgroundColor: 'currentColor',
    },
  },
}));

const randColor = () => {
  return (
    "#" +
    Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, "0")
      .toUpperCase()
  );
};

const SLIDER_START_YEAR = 2011
const SLIDER_END_YEAR = 2028
const SLIDER_DEFAULT_VALUE = [2018, 2028]

const Clusters = () => {
  const {innerWidth: width} = window;
  const {state, dispatch}: any = useContext(authContext);
  const [year, setYear] = React.useState<number[]>(SLIDER_DEFAULT_VALUE);
  const [clustersTableData, setClustersTableData]: any = useState({});
  const [chartData, setChartData]: any = useState(null);
  const debounceYear = useDebounce(year, 300);
  const [selectType, setSelectType]: any = useState(OPTIONS_TABS[0].value);
  const [selectTitle, setSelectTitle]: any = useState(OPTIONS_TABS[0].title);
  const [isLoading, setLoading]: any = useState(false);

  useEffect(() => {
    getClustersData();
  }, [state.filters.state]);

  useEffect(() => {
    if (Object.keys(clustersTableData)?.length) getClusterInfo(selectType);
  }, [
    state.filters.selectedCluster,
    state.filters.years,
    clustersTableData,
    state.filters.value,
  ]);

  const getClusterInfo = async (type: string) => {
    setLoading(true)
    const start = state.filters.years[0];
    const end = state.filters.years[1];
    const selectedValue = OPTIONS_TABS.find((o: any) => o.value === type)
    try {
      const city = JSON.stringify(
        clustersTableData[state.filters.selectedCluster?.id]?.data?.map(
          (item: any) => item.city
        )
      )
        ?.replace(/[\[\]']+/g, "")
        ?.replaceAll('"', "'");
      // @ts-ignore
      const response = await axios.get(`${baseURL}${selectedValue?.key}?state=${state.filters.state}&city=${city}&startYear=${start}&endYear=${end}&flag=trend`);
      // @ts-ignore
      const data = generateChartData(response.data?.cube, selectedValue?.id);
      setChartData(data);
      setSelectTitle(selectedValue?.title);
      setLoading(false)
    } catch (error) {
      console.log('error', error);
    }
  };

  function valuetext(value: number) {
    return `${value}`;
  }

  function valueLabelFormat(value: number) {
    return value;
  }

  useEffect(() => {
    if (year) {
      setChartData(null);
      dispatch({
        type: 'APPLY_FILTER',
        payload: {
          ...state.filters,
          years: year,
        },
      });
    }
  }, [debounceYear]);

  const handleYearChange = (event: Event, newValue: number | number[]) => {
    setYear(newValue as number[]);
  };

  const getClustersData = async () => {
    try {
      const response = await axios.get(
        `${baseURL}getCluster?state=${state.filters.state}`
      );
      const data = response.data?.cube?.map((item: any) => {
        return {...item, color: randColor()};
      });
      dispatch({
        type: 'SET_CLUSTER',
        payload: {
          ...state.filters,
          selectedCluster: {id: 0, city: data[0]?.city},
        },
      });

      let newData: any = {};

      data.forEach((item: any) => {
        if (newData[item.clusters]) {
          newData = {
            ...newData,
            [item.clusters]: {
              ...newData[item.clusters],
              data: [...newData[item.clusters].data, item],
            },
          };
        } else {
          newData = {
            ...newData,
            [item.clusters]: {data: [item], color: randColor()},
          };
        }
      });
      setClustersTableData(newData);
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleClusterChange = (val: any, i: number) => {
    setChartData(null);
    dispatch({
      type: 'SET_CLUSTER',
      payload: {
        ...state.filters,
        selectedCluster: {id: i, city: val?.city ? val?.city : ''},
      },
    });
  };

  const handleTypeChange = async (event: SelectChangeEvent) => {
    setSelectType(event.target.value as string)
    await getClusterInfo(event.target.value as string)
  };

  return (
    <div className='cluster-container'>
      <div className='cluster-container__heading'>
        {state.filters?.state} city level Clusters{' '}
      </div>
      <div className='cluster-container__filterContainer'>
        <Grid container spacing={2}>
          <Grid item xs={2.5}>
            <FormControl sx={{m: 1}} fullWidth variant='standard'>
              <InputLabel shrink id='demo-simple-select-label'>
                Select Indicator {selectType}
              </InputLabel>
              <Select
                className='cluster-container__filterContainer__selectCluster'
                onChange={handleTypeChange}
                value={selectType}
                size='small'
              >
                {OPTIONS_TABS.filter((f: any) => f.group === null).map((data: any) => (
                  <MenuItem key={data.id} value={data.value}>{data.value}</MenuItem>
                ))}
                <Divider style={{width: '90%', marginLeft: '5%', marginRight: '5%'}}> Permits </Divider>
                {OPTIONS_TABS.filter((f: any) => f.group === 'Building Permits').map((data: any) => (
                  <MenuItem key={data.id} value={data.value}>{data.value}</MenuItem>
                ))}
                <Divider style={{width: '90%', marginLeft: '5%', marginRight: '5%'}}> Valuation Of Permits</Divider>
                {OPTIONS_TABS.filter((f: any) => f.group === 'Valuation Permits').map((data: any) => (
                  <MenuItem key={data.id} value={data.value}>{data.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={9.5}>
            <FormControl sx={{m: 1}} fullWidth variant='standard'>
              <InputLabel id='demo-simple-select-label' shrink>
                Select cluster
              </InputLabel>
              <Select
                className='cluster-container__filterContainer__selectCluster'
                value={state?.filters?.selectedCluster?.id}
                label='Select cluster'
                autoWidth={true}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                {Object.keys(clustersTableData)?.map(
                  (state: any, index: number) => (
                    <MenuItem
                      className='cluster-container__filterContainer__selectCluster__clusterItem'
                      style={{width: `calc(${width} - 500px)`}}
                      value={index}
                      key={index}
                      onClick={() => handleClusterChange(state, index)}
                    >
                      {clustersTableData[state]?.data?.map((val: any, i: number) =>
                        clustersTableData[state]?.data?.length - 1 === i
                          ? val.city
                          : `${val.city}, `
                      )}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
          <FormControl sx={{m: 1, pr: 1.5}} fullWidth>
            <div className='cluster-container__filterContainer__sliderHead'>
              Choose range in years
            </div>
            <br></br>
            <IOSSlider
              valueLabelFormat={valueLabelFormat}
              getAriaValueText={valuetext}
              value={year}
              min={SLIDER_START_YEAR}
              max={SLIDER_END_YEAR}
              step={1}
              onChange={handleYearChange}
              valueLabelDisplay='on'
              // marks={years}
            />
          </FormControl>
        </Grid>
      </div>
      <div className='cluster-container__chartContainer'>
        {
          isLoading ? <Bars height='400' ariaLabel='bars-loading' wrapperClass='spinner-progress-bar'
                            visible={true}/> : chartData === null ? (
            <div className='cluster-container__chartContainer__isLoading'>
              <CircularProgress color='inherit'/>
            </div>
          ) : chartData?.length ? (
            <Chart
              chartData={chartData}
              city={clustersTableData[
                state.filters.selectedCluster?.id
                ]?.data?.map((item: any) => item.city).sort()}
              title={selectTitle}
            />
          ) : (
            <div className='cluster-container__chartContainer__noData'>
              No Data Available
            </div>
          )
        }

      </div>
      <div className='cluster-container__mapAndTableContainer'>
        <div className='cluster-container__mapAndTableContainer__mapAndLegendContainer'>
          <div className='cluster-container__mapAndTableContainer__mapAndLegendContainer__mapContainer'>
            <Map
              clusterData={clustersTableData}
              stateCord={state.filters.stateCord}
            />
          </div>
          <div className='cluster-container__mapAndTableContainer__mapAndLegendContainer__legendContainer'>
            {Object.keys(clustersTableData).map((item: any, i: number) => (
              <div
                key={i}
                className='cluster-container__mapAndTableContainer__mapAndLegendContainer__legendContainer__inner'
              >
                <div
                  className='cluster-container__mapAndTableContainer__mapAndLegendContainer__legendContainer__inner__color'
                  style={{
                    border: `2px solid ${clustersTableData[item]?.color}`,
                  }}
                ></div>
                <span style={{color: '#fff'}}>{i}</span>
              </div>
            ))}
          </div>
        </div>
        <div className='cluster-container__mapAndTableContainer__tableContainer'>
          <Paper sx={{overflow: 'hidden', width: '100%'}}>
            <TableContainer component={Paper} sx={{maxHeight: 500}}>
              <Table stickyHeader aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className='cluster-container__mapAndTableContainer__tableContainer__tableStickyHeaderCell'></TableCell>
                    <TableCell className='cluster-container__mapAndTableContainer__tableContainer__tableHeaderCell'>
                      City Clusters
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(clustersTableData).map((row: any) => (
                    <TableRow
                      key={row}
                      className='cluster-container__mapAndTableContainer__tableContainer__row'
                    >
                      <TableCell
                        className='cluster-container__mapAndTableContainer__tableContainer__tableStickyBodyCell'
                        component='th'
                        scope='row'
                        align='center'
                      >
                        {row}
                      </TableCell>
                      <TableCell className='cluster-container__mapAndTableContainer__tableContainer__tableBodyCell'>
                        {clustersTableData[row]?.data?.map(
                          (val: any, i: number) =>
                            clustersTableData[row]?.data?.length - 1 === i
                              ? val.city
                              : `${val.city}, `
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <ul className='cluster-container__mapAndTableContainer__tableContainer__bottomText'>
            <li>
              Clustering of cities based on Population growth, Population
              Demographics (i.e population by race),Total household income &
              Occupation.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Clusters;
